import type { AnalyticsEvent } from '@/domain/analyticsEvent';
import type { AnalyticsGateway } from '@/domain/analyticsGateway';
import { logger } from '@/lib/logger';

export default class AnalyticsEventGateway implements AnalyticsGateway {
  private readonly url: string;
  private buffer: AnalyticsEvent[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;
  private flushedOnUnload = false;
  private isFlushing = false;

  constructor({ endpoint, orgId }: { endpoint: string; orgId: string }) {
    this.url = `${endpoint}/orgs/${orgId}/analytics-events`;

    // Flush buffered events when the page becomes hidden (tab switch, minimize, navigation)
    document.addEventListener('visibilitychange', this.handleVisibilityChange);

    // pagehide is a fallback for older Safari which doesn't reliably fire visibilitychange
    window.addEventListener('pagehide', this.flushOnUnload);

    // Periodically flush buffered events to avoid holding them indefinitely
    this.flushTimer = setInterval(() => this.flush(), FLUSH_INTERVAL_MS);
  }

  /** Flushes remaining events, stops the timer, and removes event listeners. */
  destroy(): void {
    void this.flush();

    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }

    document.removeEventListener('visibilitychange', this.handleVisibilityChange);
    window.removeEventListener('pagehide', this.flushOnUnload);
  }

  /** Drains the buffer and sends all buffered events to the analytics endpoint. */
  async flush(): Promise<void> {
    if (this.buffer.length === 0 || this.isFlushing) {
      return;
    }

    this.isFlushing = true;
    const events = this.buffer;
    this.buffer = [];

    try {
      await this.sendWithRetry(events);
    } finally {
      this.isFlushing = false;
      if (this.buffer.length >= FLUSH_THRESHOLD) {
        void this.flush();
      }
    }
  }

  /** Synchronous fire-and-forget flush via sendBeacon for pre-navigation scenarios. */
  flushViaBeacon(): void {
    if (this.buffer.length === 0) {
      return;
    }

    const payload = JSON.stringify({ events: this.buffer });
    this.buffer = [];
    this.sendViaBeacon(payload);
  }

  /** Adds an event to the buffer, flushing immediately if the threshold is reached. */
  publish(event: AnalyticsEvent): void {
    this.buffer.push(event);

    if (this.buffer.length >= FLUSH_THRESHOLD) {
      void this.flush();
    }
  }

  // Sends events with up to MAX_RETRY_ATTEMPTS using exponential backoff.
  //
  // Retry behavior by response type:
  //   HTTP 200 + FailedRecordCount 0  → success, done
  //   HTTP 200 + FailedRecordCount >0 → re-queue only failed events, retry
  //   HTTP 429                        → respect Retry-After header, retry
  //   HTTP 5xx / network error        → retry whole batch
  //   HTTP 4xx (except 429)           → non-retryable, drop events
  //
  // API Gateway proxies to Kinesis Data Streams, which returns HTTP 200 even
  // for partial failures. The response body contains a FailedRecordCount and
  // per-record results that must be inspected to identify failed events.
  private async sendWithRetry(events: AnalyticsEvent[]): Promise<void> {
    let remaining = events;

    for (let attempt = 0; attempt < MAX_RETRY_ATTEMPTS; attempt++) {
      try {
        const response = await fetch(this.url, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ events: remaining }),
        });

        if (response.ok) {
          const body: KinesisResponse = await response.json();

          if (body.FailedRecordCount === 0) {
            return;
          }

          // Re-queue only the events whose Records entry has an ErrorCode
          remaining = this.extractFailedEvents(remaining, body.Records);
          logger.warn('Partial Kinesis failure, re-queuing failed events', {
            failed: body.FailedRecordCount,
            total: events.length,
          });
          continue;
        }

        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After');
          let delay = BASE_RETRY_DELAY_MS * 2 ** attempt;

          if (retryAfter) {
            const seconds = parseInt(retryAfter, 10);
            if (!isNaN(seconds)) {
              delay = seconds * 1_000;
            }
          }

          await this.delay(delay);
          continue;
        }

        if (response.status >= 500) {
          await this.delay(BASE_RETRY_DELAY_MS * 2 ** attempt);
          continue;
        }

        // 4xx (except 429) — non-retryable, drop events
        logger.warn('Analytics request failed with non-retryable status', { status: response.status });
        return;
      } catch {
        if (attempt < MAX_RETRY_ATTEMPTS - 1) {
          await this.delay(BASE_RETRY_DELAY_MS * 2 ** attempt);
        }
      }
    }

    logger.warn('Analytics max retries exhausted, dropping events', { count: remaining.length });
  }

  // Records array is positionally aligned with the events array sent in the request.
  // See https://docs.aws.amazon.com/kinesis/latest/APIReference/API_PutRecords.html
  private extractFailedEvents(events: AnalyticsEvent[], records: KinesisRecord[]): AnalyticsEvent[] {
    const failed: AnalyticsEvent[] = [];
    for (let i = 0; i < records.length; i++) {
      if (records[i].ErrorCode) {
        failed.push(events[i]);
      }
    }
    return failed;
  }

  private handleVisibilityChange = (): void => {
    if (document.visibilityState === 'hidden') {
      this.flushOnUnload();
    } else {
      this.flushedOnUnload = false;
    }
  };

  // Guard flag prevents duplicate flushes when both visibilitychange and pagehide fire.
  private flushOnUnload = (): void => {
    if (this.flushedOnUnload || this.buffer.length === 0) {
      return;
    }
    this.flushedOnUnload = true;

    const payload = JSON.stringify({ events: this.buffer });
    this.buffer = [];
    this.sendViaBeacon(payload);
  };

  // Uses sendBeacon for reliability during page unload and pre-navigation
  // scenarios (fire-and-forget, no partial failure handling). Falls back to
  // fetch with keepalive if sendBeacon fails (e.g. payload too large).
  private sendViaBeacon(payload: string): void {
    // text/plain avoids a CORS preflight that would fail during page unload.
    const blob = new Blob([payload], { type: 'text/plain' });
    const sent = navigator.sendBeacon(this.url, blob);

    if (!sent) {
      void fetch(this.url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: payload,
        keepalive: true,
      });
    }
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }
}

const FLUSH_INTERVAL_MS = 45_000;
const FLUSH_THRESHOLD = 10;
export const MAX_RETRY_ATTEMPTS = 3;
export const BASE_RETRY_DELAY_MS = 1_000;

// API Gateway → Kinesis response shape.
// See https://docs.aws.amazon.com/kinesis/latest/APIReference/API_PutRecords.html#API_PutRecords_ResponseSyntax
interface KinesisResponse {
  FailedRecordCount: number;
  Records: KinesisRecord[];
}

interface KinesisRecord {
  SequenceNumber?: string;
  ShardId?: string;
  ErrorCode?: string;
  ErrorMessage?: string;
}
